import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FileEdit, Calendar, Building2 } from "lucide-react";
const DEPT_COLORS = {
  Maths: {
    bg: "#dbeafe",
    color: "#1e40af"
  },
  Science: {
    bg: "#dcfce7",
    color: "#166534"
  },
  Tamil: {
    bg: "#fef9c3",
    color: "#854d0e"
  },
  English: {
    bg: "#ede9fe",
    color: "#5b21b6"
  },
  Social: {
    bg: "#fce7f3",
    color: "#9d174d"
  },
  Admin: {
    bg: "#f1f5f9",
    color: "#475569"
  },
  "Bio Maths": {
    bg: "#d1fae5",
    color: "#065f46"
  },
  "Computer Science": {
    bg: "#dbeafe",
    color: "#1e3a8a"
  },
  "Art & Science": {
    bg: "#fae8ff",
    color: "#86198f"
  },
  "Commerce": {
    bg: "#ffedd5",
    color: "#9a3412"
  }
};
const statusCfg = {
  present: {
    bg: "#dcfce7",
    color: "#166534",
    border: "#86efac",
    icon: "fa-check-circle",
    label: "Present"
  },
  late: {
    bg: "#fef9c3",
    color: "#854d0e",
    border: "#fde68a",
    icon: "fa-clock",
    label: "Late"
  },
  leave: {
    bg: "#fee2e2",
    color: "#991b1b",
    border: "#fca5a5",
    icon: "fa-calendar-times",
    label: "Leave"
  },
  absent: {
    bg: "#f1f5f9",
    color: "#475569",
    border: "#e2e8f0",
    icon: "fa-times-circle",
    label: "Absent"
  }
};
export default function StaffAttendance() {
  const navigate = useNavigate();
  const todayISO = new Date().toISOString().split("T")[0];
  const [selectedDate, setSelectedDate] = useState(todayISO);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [adminRemark, setAdminRemark] = useState("");
  useEffect(() => {
    const fetchRecords = async () => {
      setLoading(true);
      setFilter("all");
      try {
        const r = await fetch(`https://schoolcrm-fzcv.onrender.com/api/staff-attendance/today?date=${selectedDate}`);
        if (r.ok) {
          const data = await r.json();
          setRecords(Array.isArray(data) ? data : []);
        } else {
          setRecords([]);
        }
      } catch (err) {
        console.error("Error fetching staff attendance:", err);
        setRecords([]);
      } finally {
        setLoading(false);
      }
    };
    fetchRecords();
  }, [selectedDate]);
  useEffect(() => {
    const fetchLeaveRequests = async () => {
      try {
        const r = await fetch("https://schoolcrm-fzcv.onrender.com/api/leave-requests/pending");
        if (r.ok) {
          const data = await r.json();
          setLeaveRequests(data);
        }
      } catch (err) {
        console.error("Error fetching leave requests:", err);
      }
    };
    fetchLeaveRequests();
  }, []);
  const counts = {
    present: records.filter(r => r.status === "present").length,
    late: records.filter(r => r.status === "late").length,
    leave: records.filter(r => r.status === "leave").length,
    absent: records.filter(r => r.status === "absent").length
  };
  const filtered = filter === "all" ? records : records.filter(r => r.status === filter);
  const getStaffName = rec => rec.staffId?.name || rec.name || "Unknown";
  const getStaffDept = rec => rec.staffId?.dept || rec.dept || "";
  const getStaffRole = rec => rec.staffId?.role || rec.role || "";
  const dateLabel = new Date(selectedDate + "T00:00:00").toLocaleDateString("en-IN", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric"
  });
  const isToday = selectedDate === todayISO;
  const shiftDate = days => {
    const d = new Date(selectedDate + "T00:00:00");
    d.setDate(d.getDate() + days);
    setSelectedDate(d.toISOString().split("T")[0]);
  };
  const handleApprove = async id => {
    try {
      const r = await fetch(`https://schoolcrm-fzcv.onrender.com/api/leave-requests/${id}/approve`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          adminRemark
        })
      });
      if (r.ok) {
        alert("Leave request approved successfully!");
        setShowLeaveModal(false);
        setAdminRemark("");
        setSelectedRequest(null);
        const requestsRes = await fetch("https://schoolcrm-fzcv.onrender.com/api/leave-requests/pending");
        if (requestsRes.ok) {
          setLeaveRequests(await requestsRes.json());
        }
        const attendanceRes = await fetch(`https://schoolcrm-fzcv.onrender.com/api/staff-attendance/today?date=${selectedDate}`);
        if (attendanceRes.ok) {
          setRecords(await attendanceRes.json());
        }
      }
    } catch (err) {
      console.error("Error approving leave:", err);
      alert("Failed to approve leave request");
    }
  };
  const handleReject = async id => {
    try {
      const r = await fetch(`https://schoolcrm-fzcv.onrender.com/api/leave-requests/${id}/reject`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          adminRemark
        })
      });
      if (r.ok) {
        alert("Leave request rejected!");
        setShowLeaveModal(false);
        setAdminRemark("");
        setSelectedRequest(null);
        const requestsRes = await fetch("https://schoolcrm-fzcv.onrender.com/api/leave-requests/pending");
        if (requestsRes.ok) {
          setLeaveRequests(await requestsRes.json());
        }
      }
    } catch (err) {
      console.error("Error rejecting leave:", err);
      alert("Failed to reject leave request");
    }
  };
  return <div style={{
    minHeight: "100vh",
    background: "#f1f5f9",
    fontFamily: "'Poppins', sans-serif"
  }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800&display=swap');
        @import url('https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0-beta3/css/all.min.css');
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { background: #f1f5f9; font-family: 'Poppins', sans-serif; }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: #f1f5f9; }
        ::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }
      `}</style>

      {/* Header */}
      <div style={{
      background: "linear-gradient(135deg, #0f2027, #2c5364, #294f5f)",
      padding: "16px 28px",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      borderBottom: "1px solid #d97706",
      flexWrap: "wrap",
      gap: 12
    }}>
        <div style={{
        display: "flex",
        alignItems: "center",
        gap: 14
      }}>
          <div style={{
          width: 44,
          height: 44,
          borderRadius: 14,
          background: "linear-gradient(135deg, #d97706, #f59e0b)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 22
        }}>
            <i className="fas fa-user-clock" style={{
            color: "#fff"
          }}></i>
          </div>
          <div>
            <div style={{
            fontWeight: 800,
            fontSize: 18,
            color: "#fff"
          }}>Staff Attendance</div>
            <div style={{
            fontSize: 11,
            color: "#d97706"
          }}>Track daily attendance</div>
          </div>
        </div>
        <div style={{
        display: "flex",
        gap: 10
      }}>
          <button onClick={() => setShowLeaveModal(true)} style={{
          background: "linear-gradient(135deg, #f59e0b, #d97706)",
          color: "#fff",
          border: "none",
          padding: "8px 18px",
          borderRadius: 10,
          fontSize: 12,
          fontWeight: 600,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: 6
        }}>
            <i className="fas fa-inbox"></i> Leave Requests ({leaveRequests.length})
          </button>
          <button onClick={() => navigate("/admin")} style={{
          background: "rgba(255,255,255,0.15)",
          border: "none",
          color: "#fff",
          padding: "8px 18px",
          borderRadius: 10,
          fontSize: 12,
          fontWeight: 600,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: 6
        }}>
            <i className="fas fa-arrow-left"></i> Back
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div style={{
      padding: "24px 28px"
    }}>
        <div style={{
        background: "#fff",
        borderRadius: 22,
        overflow: "hidden",
        boxShadow: "0 1px 12px rgba(0,0,0,0.04)"
      }}>
          
          {/* Date Picker Bar */}
          <div style={{
          background: "#f8fafc",
          borderBottom: "1px solid #e2e8f0",
          padding: "16px 24px",
          display: "flex",
          alignItems: "center",
          gap: 12,
          flexWrap: "wrap"
        }}>
            <span style={{
            fontSize: 11,
            fontWeight: 700,
            color: "#64748b",
            textTransform: "uppercase"
          }}>
              <i className="fas fa-calendar-alt" style={{
              marginRight: 5,
              color: "#d97706"
            }}></i>Select Date
            </span>

            <button onClick={() => shiftDate(-1)} style={{
            background: "#fff",
            border: "1px solid #e2e8f0",
            borderRadius: 8,
            width: 32,
            height: 32,
            cursor: "pointer"
          }}>
              <i className="fas fa-chevron-left"></i>
            </button>

            <input type="date" value={selectedDate} max={todayISO} onChange={e => e.target.value && setSelectedDate(e.target.value)} style={{
            border: "1px solid #d97706",
            borderRadius: 9,
            padding: "6px 12px",
            fontSize: 13,
            fontFamily: "'Poppins',sans-serif",
            outline: "none",
            cursor: "pointer"
          }} />

            <button onClick={() => shiftDate(1)} disabled={isToday} style={{
            background: "#fff",
            border: "1px solid #e2e8f0",
            borderRadius: 8,
            width: 32,
            height: 32,
            cursor: isToday ? "not-allowed" : "pointer",
            opacity: isToday ? 0.5 : 1
          }}>
              <i className="fas fa-chevron-right"></i>
            </button>

            {!isToday && <button onClick={() => setSelectedDate(todayISO)} style={{
            background: "linear-gradient(135deg, #d97706, #f59e0b)",
            color: "#fff",
            border: "none",
            borderRadius: 8,
            padding: "6px 14px",
            fontSize: 11,
            fontWeight: 700,
            cursor: "pointer"
          }}>
                Today
              </button>}

            <div style={{
            marginLeft: "auto",
            fontSize: 12,
            color: "#94a3b8"
          }}>
              {records.length} record{records.length !== 1 ? "s" : ""} found
            </div>
          </div>

          {/* Summary Count Tiles */}
          <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: 0,
          borderBottom: "1px solid #f1f5f9"
        }}>
            {[{
            key: "present",
            label: "Present",
            color: "#16a34a",
            bg: "#f0fdf4",
            icon: "fa-check-circle"
          }, {
            key: "late",
            label: "Late",
            color: "#d97706",
            bg: "#fffbeb",
            icon: "fa-clock"
          }, {
            key: "leave",
            label: "On Leave",
            color: "#dc2626",
            bg: "#fef2f2",
            icon: "fa-calendar-times"
          }, {
            key: "absent",
            label: "Absent",
            color: "#475569",
            bg: "#f8fafc",
            icon: "fa-times-circle"
          }].map(({
            key,
            label,
            color,
            bg,
            icon
          }) => {
            const active = filter === key;
            return <div key={key} onClick={() => setFilter(active ? "all" : key)} style={{
              padding: "20px 10px",
              background: active ? bg : "#fff",
              cursor: "pointer",
              textAlign: "center",
              transition: "background 0.15s"
            }}>
                  <div style={{
                fontSize: 36,
                fontWeight: 800,
                color: active ? color : "#1e293b",
                lineHeight: 1
              }}>{counts[key]}</div>
                  <div style={{
                fontSize: 11,
                color: active ? color : "#94a3b8",
                fontWeight: 700,
                textTransform: "uppercase",
                marginTop: 6,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 6
              }}>
                    <i className={`fas ${icon}`}></i> {label}
                  </div>
                  {active && <div style={{
                width: 30,
                height: 3,
                background: color,
                borderRadius: 2,
                margin: "8px auto 0"
              }} />}
                </div>;
          })}
          </div>

          {/* Filter Pills */}
          <div style={{
          padding: "12px 20px",
          borderBottom: "1px solid #f1f5f9",
          display: "flex",
          gap: 8,
          flexWrap: "wrap",
          background: "#fafcff"
        }}>
            <span style={{
            fontSize: 10,
            color: "#94a3b8",
            fontWeight: 600,
            marginRight: 4
          }}>FILTER:</span>
            {["all", "present", "late", "leave", "absent"].map(f => <button key={f} onClick={() => setFilter(f)} style={{
            padding: "4px 14px",
            borderRadius: 20,
            fontSize: 11,
            cursor: "pointer",
            fontWeight: filter === f ? 700 : 400,
            border: filter === f ? "none" : "1px solid #e2e8f0",
            background: filter === f ? "linear-gradient(135deg, #0f2027, #2c5364)" : "transparent",
            color: filter === f ? "#fff" : "#94a3b8",
            textTransform: "capitalize"
          }}>
                {f === "all" ? `All (${records.length})` : `${f} (${counts[f] ?? 0})`}
              </button>)}
          </div>

          {/* Records List */}
          <div style={{
          maxHeight: "calc(100vh - 320px)",
          overflowY: "auto"
        }}>
            {loading ? <div style={{
            padding: 80,
            textAlign: "center",
            color: "#94a3b8"
          }}>
                <i className="fas fa-spinner fa-pulse" style={{
              fontSize: 32,
              marginBottom: 12,
              display: "block"
            }}></i>
                Loading attendance for {dateLabel}...
              </div> : filtered.length === 0 ? <div style={{
            padding: 80,
            textAlign: "center",
            color: "#94a3b8"
          }}>
                <i className="fas fa-calendar-times" style={{
              fontSize: 48,
              marginBottom: 16,
              display: "block"
            }}></i>
                <div style={{
              fontWeight: 600,
              marginBottom: 6,
              fontSize: 16
            }}>
                  {records.length === 0 ? "No attendance records" : "No records match this filter"}
                </div>
                <div style={{
              fontSize: 13
            }}>
                  {records.length === 0 ? `No staff marked attendance on ${dateLabel}` : "Try selecting a different filter"}
                </div>
              </div> : <div style={{
            padding: "20px 24px",
            display: "grid",
            gap: 12
          }}>
                {filtered.map((rec, idx) => {
              const name = getStaffName(rec);
              const dept = getStaffDept(rec);
              const role = getStaffRole(rec);
              const sc = statusCfg[rec.status] || statusCfg.absent;
              const dc = DEPT_COLORS[dept] || {
                bg: "#f1f5f9",
                color: "#475569"
              };
              return <div key={rec._id || idx} style={{
                border: `1px solid ${sc.border}`,
                borderRadius: 14,
                overflow: "hidden",
                background: "#fff"
              }}>
                      <div style={{
                  display: "flex",
                  alignItems: "center"
                }}>
                        <div style={{
                    width: 5,
                    alignSelf: "stretch",
                    background: sc.color,
                    flexShrink: 0
                  }} />
                        <div style={{
                    flex: 1,
                    padding: "14px 18px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: 12
                  }}>
                          <div style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 14
                    }}>
                            <div style={{
                        width: 48,
                        height: 48,
                        borderRadius: 14,
                        background: dc.bg,
                        color: dc.color,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 800,
                        fontSize: 16,
                        flexShrink: 0
                      }}>
                              {name.slice(0, 2).toUpperCase()}
                            </div>
                            <div>
                              <div style={{
                          fontWeight: 700,
                          fontSize: 14,
                          color: "#1e293b"
                        }}>{name}</div>
                              <div style={{
                          fontSize: 12,
                          color: "#64748b",
                          marginTop: 4,
                          display: "flex",
                          alignItems: "center",
                          gap: 8
                        }}>
                                {role && <span>{role}</span>}
                                {dept && <span style={{
                            background: dc.bg,
                            color: dc.color,
                            padding: "2px 10px",
                            borderRadius: 20,
                            fontWeight: 600,
                            fontSize: 10
                          }}>{dept}</span>}
                              </div>
                            </div>
                          </div>
                          <div style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 16
                    }}>
                            {rec.time && <div style={{
                        fontSize: 12,
                        color: "#94a3b8",
                        display: "flex",
                        alignItems: "center",
                        gap: 5
                      }}>
                                <i className="far fa-clock"></i> {rec.time}
                              </div>}
                            <span style={{
                        background: sc.bg,
                        color: sc.color,
                        border: `1px solid ${sc.border}`,
                        padding: "6px 15px",
                        borderRadius: 20,
                        fontSize: 12,
                        fontWeight: 700,
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6
                      }}>
                              <i className={`fas ${sc.icon}`}></i> {sc.label}
                            </span>
                          </div>
                        </div>
                      </div>
                      {rec.note && <div style={{
                  paddingLeft: 76,
                  paddingRight: 20,
                  paddingBottom: 12,
                  fontSize: 11,
                  color: "#94a3b8",
                  fontStyle: "italic"
                }}>
                          <FileEdit className="inline w-[1em] h-[1em] align-[-0.125em]" /> {rec.note}
                        </div>}
                    </div>;
            })}
              </div>}
          </div>
        </div>
      </div>

      {/* Leave Requests Modal */}
      {showLeaveModal && <div style={{
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,0.55)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 1000,
      overflow: "auto"
    }}>
          <div style={{
        background: "#fff",
        borderRadius: 16,
        width: 600,
        maxHeight: "80vh",
        overflow: "auto",
        boxShadow: "0 24px 64px rgba(0,0,0,0.3)"
      }}>
            <div style={{
          background: "linear-gradient(135deg,#0f2027,#2c5364)",
          padding: "16px 20px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          position: "sticky",
          top: 0
        }}>
              <div style={{
            fontWeight: 700,
            fontSize: 15,
            color: "#fff"
          }}>
                <i className="fas fa-inbox"></i> Pending Leave Requests
              </div>
              <button onClick={() => {
            setShowLeaveModal(false);
            setSelectedRequest(null);
            setAdminRemark("");
          }} style={{
            background: "rgba(255,255,255,0.15)",
            border: "none",
            color: "#fff",
            width: 28,
            height: 28,
            borderRadius: "50%",
            cursor: "pointer"
          }}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div style={{
          padding: "16px"
        }}>
              {leaveRequests.length === 0 ? <div style={{
            textAlign: "center",
            padding: "40px",
            color: "#94a3b8"
          }}>
                  <i className="fas fa-check-circle" style={{
              fontSize: 40,
              marginBottom: 10,
              color: "#22c55e"
            }}></i>
                  <div>No pending leave requests</div>
                </div> : leaveRequests.map(req => <div key={req._id} style={{
            border: "1px solid #e2e8f0",
            borderRadius: 12,
            padding: "16px",
            marginBottom: 12
          }}>
                    <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 8
            }}>
                      <div style={{
                fontWeight: 600,
                fontSize: 14,
                color: "#1e293b"
              }}>{req.staffName}</div>
                      <span style={{
                background: "#fef3c7",
                color: "#92400e",
                padding: "2px 8px",
                borderRadius: 12,
                fontSize: 10,
                fontWeight: 600
              }}>
                        <i className="fas fa-clock"></i> Pending
                      </span>
                    </div>
                    <div style={{
              fontSize: 12,
              color: "#64748b",
              marginBottom: 4
            }}><Calendar className="inline w-[1em] h-[1em] align-[-0.125em]" /> {new Date(req.date).toLocaleDateString()}</div>
                    <div style={{
              fontSize: 12,
              color: "#64748b",
              marginBottom: 12
            }}><Building2 className="inline w-[1em] h-[1em] align-[-0.125em]" /> {req.staffDept}</div>
                    {req.reason && <div style={{
              fontSize: 12,
              color: "#475569",
              marginBottom: 12,
              background: "#f8fafc",
              padding: "8px",
              borderRadius: 8
            }}><FileEdit className="inline w-[1em] h-[1em] align-[-0.125em]" /> {req.reason}</div>}
                    <div style={{
              marginTop: 12
            }}>
                      <textarea placeholder="Admin remarks (optional)" value={selectedRequest === req._id ? adminRemark : ""} onChange={e => {
                setSelectedRequest(req._id);
                setAdminRemark(e.target.value);
              }} style={{
                width: "100%",
                padding: "8px",
                borderRadius: 8,
                fontSize: 12,
                border: "1px solid #e2e8f0",
                marginBottom: 10,
                fontFamily: "'Poppins',sans-serif",
                resize: "vertical"
              }} rows={2} />
                      <div style={{
                display: "flex",
                gap: 10
              }}>
                        <button onClick={() => handleApprove(req._id)} style={{
                  flex: 1,
                  background: "#22c55e",
                  color: "#fff",
                  border: "none",
                  padding: "8px",
                  borderRadius: 8,
                  cursor: "pointer",
                  fontWeight: 600
                }}>
                          <i className="fas fa-check"></i> Approve
                        </button>
                        <button onClick={() => handleReject(req._id)} style={{
                  flex: 1,
                  background: "#ef4444",
                  color: "#fff",
                  border: "none",
                  padding: "8px",
                  borderRadius: 8,
                  cursor: "pointer",
                  fontWeight: 600
                }}>
                          <i className="fas fa-times"></i> Reject
                        </button>
                      </div>
                    </div>
                  </div>)}
            </div>
          </div>
        </div>}
    </div>;
}